import { isServiceRequestError, interceptResponses } from './service-request';
import { getSessionInfo } from 'src/services/session-service';

/**
 * Track information about the user session
 */
const session = { value: null as SessionInfo };
export default session;

/**
 * A class the stores information about the user's session
 */
class SessionInfo {
	/**
	 * The date and time that the user's session will expire
	 */
	expiration: Date = null;

	/**
	 * The user's unique ID
	 */
	userId: string = null;

	/**
	 * The session's unique ID
	 */
	sessionId: string = null;

	/**
	 * The current organization's unique ID
	 */
	organizationId: string = null;

	/**
	 * A unique ID for use in telemetry
	 */
	telemetrySessionId: string = null;

	/**
	 * The session token to send to Wordpress
	 * TODO: [MFT] This should be removed with the Wordpress plugin is updated to use the oauth process.
	 */
	sessionToken: string = null;
}

/**
 * Gets or establishes the user's session
 */
export async function getActiveSession(): Promise<SessionInfo> {
	// If the session has not yet been established, then attempt to fetch from the server
	if (!session.value) {
		try {
			await getSessionInfo().then(sessionInfo => {
				if (sessionInfo) {
					session.value = updateSession(sessionInfo);
				}
				else {
					throw new SessionError('User is not authenticated');
				}
			});
		}
		catch (err) {
			if (isServiceRequestError(err) && err.statusCode === 401)
				throw new SessionError('User is not authenticated');
			throw new SessionError('Unable to get session');
		}
	}

	// Check if the user's session has expired
	const now = new Date();
	if (!session.value.expiration || session.value.expiration <= now) {
		throw new SessionError('Session expired');
	}

	return session.value;
}

/**
 * An error that occurs when attempting to get or validate the user's session
 */
class SessionError extends Error {
	constructor(message: string) {
		super(message);
	}
}

/**
 * Updates the session info with the given data,
 * either from a header or the `/session/info` service request
 * @param  {{[key:string]:string}} data The new session data
 * @returns The new or updated session info object
 */
function updateSession(data: { [key: string]: string }): SessionInfo {
	// If the session has not already been established, then create a new session object
	if (!session.value)
		session.value = new SessionInfo();

	// Update the session with the given data
	Object.keys(data).forEach(key => {
		const value = data[key];
		if (key.toLowerCase() === 'expiration') {
			session.value.expiration = new Date(value);
		}
		else if (key.toLowerCase() === 'userid') {
			session.value.userId = value;
		}
		else if (key.toLowerCase() === 'sessionid') {
			session.value.sessionId = value;
		}
		else if (key.toLowerCase() === 'organizationid') {
			session.value.organizationId = value;
		}
		else if (key.toLowerCase() === 'telemetrysessionid') {
			session.value.telemetrySessionId = value;
		}
		else if (key.toLowerCase() === 'sessiontoken') {
			session.value.sessionToken = value;
		}
	});

	return session.value;
}

interceptResponses(async response => {
	// Update session info if the server sends an 'X-SessionInfo' header,
	// indicating that the session was either established or updated
	const sessionInfoHeader = response.headers.get('X-SessionInfo');
	if (sessionInfoHeader) {
		const sessionInfo = parseKeyValueString(sessionInfoHeader);
		updateSession(sessionInfo);
	}
});

/**
 * Parse a string of key-value pairs (ex: `a=b;c=d;...`) and return the data as a POJO object
 * @param str The raw text to parse
 * @returns An object that contains the key/value pair data from the string
 */
function parseKeyValueString(str: string): { [key: string]: string } {
	const obj = {};
	str.split(';').map(item => {
		const idx = item.indexOf('=');
		const key = item.substring(0, idx);
		const value = item.substring(idx + 1);
		obj[key] = value;
	});
	return obj;
}
